// components/AvatarUpload/index.js - Rediseño Compacto y Horizontal
import React, { useState, useRef, useEffect } from 'react';
import Avatar from '@material-ui/core/Avatar';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Button from '@material-ui/core/Button';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import PersonIcon from '@material-ui/icons/Person';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Grid from '@material-ui/core/Grid';
import { toast } from 'react-toastify';
import { getBackendUrl } from '../../config';
import { i18n } from "../../translate/i18n";

// Import Avatars
import avatarMale from '../../assets/avatars/avatar_male.png';
import avatarRobot from '../../assets/avatars/avatar_robot.png';
import avatarAbstract from '../../assets/avatars/avatar_abstract.png';
import avatarMaleBeard from '../../assets/avatars/avatar_male_beard.png';
import avatarMaleDark from '../../assets/avatars/avatar_male_dark.png';
import avatarMaleBlonde from '../../assets/avatars/avatar_male_blonde.png';
import avatarFemaleShort from '../../assets/avatars/avatar_female_short.png';
import avatarFemaleDark from '../../assets/avatars/avatar_female_dark.png';
import avatarFemalePonytail from '../../assets/avatars/avatar_female_ponytail.png';

const backendUrl = getBackendUrl();

const useStyles = makeStyles((theme) => ({
  cardRoot: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 20px',
    borderRadius: 14,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    width: '100%',
    boxSizing: 'border-box',
    gap: 20,
    marginBottom: 16,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      textAlign: 'center',
      gap: 12,
      padding: '12px 14px',
    },
  },
  avatarWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  avatar: {
    width: 82,
    height: 82,
    cursor: 'pointer',
    borderRadius: '50%',
    border: '2px solid rgba(255, 255, 255, 0.25)',
    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.35)',
    backgroundColor: '#2d3436',
    transition: 'all 0.25s ease-in-out',
    '&:hover': {
      transform: 'scale(1.05)',
      borderColor: '#00a8ff',
      boxShadow: '0 6px 18px rgba(0, 168, 255, 0.4)',
    },
  },
  infoContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 1,
    gap: 8,
    minWidth: 0,
  },
  title: {
    fontSize: '0.85rem',
    fontWeight: 700,
    letterSpacing: '0.5px',
    color: '#e2e8f0',
    textTransform: 'uppercase',
  },
  buttonsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'center',
    },
  },
  uploadButton: {
    backgroundColor: '#00a8ff !important',
    color: '#ffffff !important',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8125rem',
    borderRadius: 8,
    padding: '6px 14px',
    boxShadow: '0 2px 6px rgba(0, 168, 255, 0.35)',
    '&:hover': {
      backgroundColor: '#0097e6 !important',
      boxShadow: '0 4px 12px rgba(0, 168, 255, 0.55)',
    },
  },
  galleryButton: {
    backgroundColor: '#6c5ce7 !important',
    color: '#ffffff !important',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8125rem',
    borderRadius: 8,
    padding: '6px 14px',
    boxShadow: '0 2px 6px rgba(108, 92, 231, 0.35)',
    '&:hover': {
      backgroundColor: '#5b4bc4 !important',
      boxShadow: '0 4px 12px rgba(108, 92, 231, 0.55)',
    },
  },
  deleteButton: {
    backgroundColor: '#ff4757 !important',
    color: '#ffffff !important',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8125rem',
    borderRadius: 8,
    padding: '6px 14px',
    boxShadow: '0 2px 6px rgba(255, 71, 87, 0.35)',
    '&:hover': {
      backgroundColor: '#e84118 !important',
      boxShadow: '0 4px 12px rgba(255, 71, 87, 0.55)',
    },
  },
  galleryAvatar: {
    width: theme.spacing(9),
    height: theme.spacing(9),
    cursor: 'pointer',
    borderRadius: '50%',
    border: '2px solid transparent',
    transition: 'all 0.2s',
    '&:hover': {
      border: '3px solid #6c5ce7',
      transform: 'scale(1.08)',
    },
  },
}));

const AvatarUploader = ({ setAvatar, avatar, companyId, onRemoveAvatar }) => {
  const classes = useStyles();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [openGallery, setOpenGallery] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!avatar) {
      setPreviewImage(null);
      setSelectedFile(null);
    }
  }, [avatar]);

  const predefinedAvatars = [
    { src: avatarMale, label: 'Male' },
    { src: avatarRobot, label: 'Robot' },
    { src: avatarAbstract, label: 'Abstract' },
    { src: avatarMaleBeard, label: 'Male Beard' },
    { src: avatarMaleDark, label: 'Male Dark' },
    { src: avatarMaleBlonde, label: 'Male Blonde' },
    { src: avatarFemaleShort, label: 'Female Short' },
    { src: avatarFemaleDark, label: 'Female Dark' },
    { src: avatarFemalePonytail, label: 'Female Ponytail' },
  ];

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(i18n.t("avatarUpload.errorFormat"));
      return;
    }

    // Validar tamaño (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error(i18n.t("avatarUpload.errorSize"));
      return;
    }

    setSelectedFile(file);
    if (setAvatar) setAvatar(file);

    const reader = new FileReader();
    reader.onload = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSelectFromGallery = async (imgSrc) => {
    try {
      const response = await fetch(imgSrc);
      const blob = await response.blob();
      const file = new File([blob], "avatar_gallery.png", { type: blob.type });

      setSelectedFile(file);
      if (setAvatar) setAvatar(file);
      setPreviewImage(imgSrc);
      setOpenGallery(false);
    } catch (error) {
      console.error("Erro ao selecionar avatar da galeria", error);
      toast.error(i18n.t("avatarUpload.errorLoad"));
    }
  };

  const handleRemove = () => {
    setPreviewImage(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (onRemoveAvatar) {
      onRemoveAvatar();
    } else if (setAvatar) {
      setAvatar(null);
    }
  };

  // Función para determinar cuál imagen mostrar
  const getImageSrc = () => {
    if (previewImage) return previewImage;
    if (avatar) {
      if (typeof avatar === 'string') {
        if (avatar.startsWith('http://') || avatar.startsWith('https://') || avatar.startsWith('data:')) {
          return avatar;
        }
        if (avatar.startsWith('default/')) {
          return `${backendUrl}/public/${avatar}`;
        }
        if (companyId) {
          return `${backendUrl}/public/company${companyId}/user/${avatar}`;
        }
        return `${backendUrl}/public/${avatar}`;
      }
    }
    return null;
  };

  const currentImageSrc = getImageSrc();
  const hasImage = Boolean(currentImageSrc || previewImage || avatar);

  return (
    <Paper className={classes.cardRoot} elevation={0}>
      <div className={classes.avatarWrapper}>
        <Tooltip title={i18n.t("avatarUpload.galleryButton")} arrow>
          <Avatar
            alt="User Avatar"
            src={currentImageSrc || undefined}
            className={classes.avatar}
            onClick={() => setOpenGallery(true)}
          >
            {!currentImageSrc && (
              <PersonIcon style={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.7)' }} />
            )}
          </Avatar>
        </Tooltip>
      </div>

      <div className={classes.infoContainer}>
        <Typography className={classes.title}>
          {i18n.t("avatarUpload.sectionTitle")}
        </Typography>

        <div className={classes.buttonsRow}>
          <input
            ref={fileInputRef}
            accept="image/*"
            type="file"
            id="avatar-upload-file"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <label htmlFor="avatar-upload-file">
            <Button
              variant="contained"
              component="span"
              size="small"
              startIcon={<CloudUploadIcon />}
              className={classes.uploadButton}
            >
              {i18n.t("avatarUpload.fileButton")}
            </Button>
          </label>

          <Button
            variant="contained"
            size="small"
            onClick={() => setOpenGallery(true)}
            startIcon={<PhotoLibraryIcon />}
            className={classes.galleryButton}
          >
            {i18n.t("avatarUpload.galleryButton")}
          </Button>

          {hasImage && (
            <Button
              variant="contained"
              size="small"
              onClick={handleRemove}
              startIcon={<DeleteOutlineIcon />}
              className={classes.deleteButton}
            >
              {i18n.t("avatarUpload.deleteButton")}
            </Button>
          )}
        </div>
      </div>

      {/* Gallery Dialog */}
      <Dialog
        open={openGallery}
        onClose={() => setOpenGallery(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{i18n.t("avatarUpload.galleryTitle")}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} justify="center">
            {predefinedAvatars.map((item, index) => (
              <Grid item key={index}>
                <Avatar
                  src={item.src}
                  className={classes.galleryAvatar}
                  onClick={() => handleSelectFromGallery(item.src)}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default AvatarUploader;
